import * as auth from "../auth.js";

const baseUrl = window.appSettings.api.uri;

const Tools = {
  getRootAPI: () => {
    if (process.env.NODE_ENV === "development") {
      return baseUrl;
    }
    // For production, you can add the production API root later
    return baseUrl;
  },
  formatNumber: (number) => {
    return number.toLocaleString();
  },

    /**
     * Download files directly - not JSON
     * @param {string} resource - API endpoint name
     * @param {object} params - Request parameters  
     * @param {string} filename - Optional override filename
     */
  downloadFile: async (resource, params, filename = null) => {
      console.log("File download request", resource, params);
      
      let fullUrl;
      let options = {
          method: "GET",
          headers: {
              ...(window.appSettings.isOidc ? {} : {
                  Authorization: auth.getAccessToken(),
              })
          }
      };

      // Handle query string parameters (same as sendRequest GET logic)
      let queryString = "";
      if (typeof params === "object" && params !== null) {
          // If params is an object, add action parameter and convert to query string
          const paramsWithAction = { ...params, action: resource };
          const queryParts = [];

          for (const key of Object.keys(paramsWithAction)) {
              const value = paramsWithAction[key];
              if (Array.isArray(value)) {
                  // Multiple parameters with same name
                  value.forEach((item) => {
                      queryParts.push(
                          `${encodeURIComponent(key)}=${encodeURIComponent(item)}`
                      );
                  });
              } else {
                  queryParts.push(
                      `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                  );
              }
          }
          queryString = queryParts.join("&");
      } else if (typeof params === "string") {
          // If params is already a string, append the action parameter
          queryString = params
              ? `${params}&action=${encodeURIComponent(resource)}`
              : `action=${encodeURIComponent(resource)}`;
      } else {
          // Just the action
          queryString = `action=${encodeURIComponent(resource)}`;
      }

      fullUrl = Tools.getRootAPI() + "/api" + (queryString ? `?${queryString}` : "");

      console.log("Downloading from:", fullUrl);

      let response;
      try {
          response = await fetch(fullUrl, options);
      } catch (e) {
          if (window.appSettings.isOidc) {
              console.error("Download error:", e);
              throw e;
          } else {
              // Auth retry logic (same as sendRequest)
              try {
                  const newToken = await auth.refreshToken();
                  options.headers.Authorization = newToken;
                  response = await fetch(fullUrl, options);
              } catch (refreshError) {
                  auth.logOut();
                  throw refreshError;
              }
          }
      }

      if (!response.ok) {
          // Handle errors (might be JSON error response)
          let errorMessage;
          try {
              const errorData = await response.json();
              errorMessage = errorData.message || JSON.stringify(errorData);
          } catch {
              errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
          alert("Download failed: " + errorMessage);
          throw new Error(errorMessage);
      }

      // Extract filename from headers or use provided/default
      let downloadFilename = filename;
      if (!downloadFilename) {
          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
              const match = contentDisposition.match(/filename="([^"]+)"/);
              downloadFilename = match ? match[1] : 'download.file';
          } else {
              downloadFilename = 'download.file';
          }
      }

      // Handle the binary download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);

      // Return download info
      const recordCount = response.headers.get('X-Record-Count');
      const fileSize = response.headers.get('Content-Length');
      
      return {
          filename: downloadFilename,
          size: fileSize ? parseInt(fileSize) : blob.size,
          recordCount: recordCount ? parseInt(recordCount) : null,
          contentType: response.headers.get('Content-Type')
      };
  },

  sendRequest: async (resource, params, method = "GET", signal = null) => {
    console.log("Got request", resource, params, method);
    
    let fullUrl;
    let options = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        ...(window.appSettings.isOidc ? {} : {
          Authorization: auth.getAccessToken(),
        })
      },
      signal: signal
    };

    if (method.toUpperCase() === "GET") {
      // Handle GET requests with query parameters
      let queryString = "";
      if (typeof params === "object" && params !== null) {
        // If params is an object (dictionary), add action parameter and convert to query string
        const paramsWithAction = { ...params, action: resource };
        const queryParts = [];

        for (const key of Object.keys(paramsWithAction)) {
          const value = paramsWithAction[key];

          if (Array.isArray(value)) {
            // If the value is an array, add multiple parameters with the same name
            value.forEach((item) => {
              queryParts.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(item)}`
              );
            });
          } else {
            queryParts.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            );
          }
        }

        queryString = queryParts.join("&");
      } else if (typeof params === "string") {
        // If params is already a string, append the action parameter
        queryString = params
          ? `${params}&action=${encodeURIComponent(resource)}`
          : `action=${encodeURIComponent(resource)}`;
      } else {
        // If params is neither an object nor a string, create a new query string with just the action
        queryString = `action=${encodeURIComponent(resource)}`;
      }

      fullUrl = Tools.getRootAPI() + "/api" + (queryString ? `?${queryString}` : "");
    } else {
      // Handle POST requests with body data
      fullUrl = Tools.getRootAPI() + "/api?action=" + encodeURIComponent(resource);
      options.body = JSON.stringify(params);
    }

    console.log("Sending to:", fullUrl);

    let response;
    console.debug("Request opts:", JSON.stringify(options, null, 4));
    try {
      response = await fetch(fullUrl, options);
    } catch (e) {
      // If this is an abort error, don't retry, just propagate it
      if (e.name === 'AbortError') {
        throw e;
      }
      
      if (window.appSettings.isOidc) {
        alert("Request error:", e);
      } else {
        // Invalid auth token
        try {
          // Retry
          const newToken = await auth.refreshToken();
          options.headers.Authorization = newToken;
          response = await fetch(fullUrl, options);
        } catch (e) {
          // If retry aborted, propagate the abort error
          if (e.name === 'AbortError') {
            throw e;
          }
          // refresh failed
          auth.logOut();
          throw e;
        }
      }
    }

    console.debug("Response:", response);
    let data = await response.json();
    if (!response.ok) {
      alert("There was an error. Message:" + JSON.stringify(data));
    }

    // Process the data if the resource is 'get-data'
    if (resource === "get-data") {
      const fieldsToConvert = [
        "agent_percent",
        "agent_performance_score",
        "agent_sentiment",
        "customer_experience_score",
        "customer_final_satisfaction",
        "customer_sentiment",
      ];

      if (Array.isArray(data.data)) {
        data.data = data.data.map((item) => {
          fieldsToConvert.forEach((field) => {
            if (
              item[field] !== null &&
              item[field] !== undefined &&
              item[field] !== ""
            ) {
              item[field] = parseFloat(item[field]);
            }
          });
          return item;
        });
      }
    }

    return data;
  },

  downloadFromPresignedUrl: async (action, params) => {
    try {
        // Use existing sendRequest to get the pre-signed URL
        const response = await Tools.sendRequest(action, params);
        
        // response should contain { downloadUrl, filename, recordCount }
        if (response.downloadUrl) {
            // Trigger download using the pre-signed URL
            const link = document.createElement('a');
            link.href = response.downloadUrl;
            link.download = response.filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            return response; // Return the full response for logging/notifications
        } else {
            throw new Error('No download URL received');
        }
    } catch (error) {
        console.error(`Download failed for ${action}:`, error);
        throw error;
    }
  },
};

export default Tools;